import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchFranchiseById } from "services/franchise";
import { STALE_TIME } from "utils/constants";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useAlert } from "context/alert";

const AuthorizationCertificate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const rowData = location.state?.rowData;
  const isDirectRedirect = location.state?.isDirectRedirect;
  console.log("rowData", rowData);

  const certificateRef = useRef();

  const getFormattedDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    const getSuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const suffix = getSuffix(day);
    return (
      <>
        This certificate will be valid upto {day}
        <sup>{suffix}</sup> {month}'{year}.
      </>
    );
  };

  const formatDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const franchiseId = localStorage.getItem("franchise_id");

  const { data: franchiseData } = useQuery(
    ["fetch-franchise-by-id"],
    () => fetchFranchiseById(franchiseId),
    {
      enabled: !!franchiseId,
      staleTime: STALE_TIME,
      select: (res) => res || [],
      onSuccess: (res) => {
        console.log("Franchise data fetched:", res);
      },
      onError: (err) => {
        console.error("Error fetching zone data:", err);
      },
    }
  );

  useEffect(() => {
    if (!isDirectRedirect) return;

    const downloadPDF = async () => {
      if (!certificateRef.current) {
        console.error("Certificate element not found");
        return;
      }

      try {
        const element = certificateRef.current;

        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          width: element.scrollWidth,
          height: element.scrollHeight,
        });

        const imgData = canvas.toDataURL("image/png", 1.0);

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const margin = 1.76; // same margin as in working button function

        const imgWidth = pdfWidth - margin * 2;
        const imgHeight = pdfHeight - margin * 2;

        pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);
        pdf.save("AuthorizationCertificate.pdf");

        showAlert("Authorization Certificate downloaded as high-quality PDF.");
        setTimeout(() => navigate(-1), 2000);
      } catch (error) {
        console.error("Error generating PDF:", error);
        showAlert("Error generating PDF. Please try again.");
      }
    };

    const timer = setTimeout(downloadPDF, 2000);
    return () => clearTimeout(timer);
  }, [navigate, isDirectRedirect]);

  return (
    <div
      style={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1123px",
        padding: "20px",
        background: "white",
      }}
    >
      <div
        style={{ ...styles.body, display: "flex", justifyContent: "center" }}
        ref={certificateRef}
      >
        <div style={styles.shadowBox}>
          <div style={styles.certificate}>
            <div style={styles.innerBorder}></div>
            <img
              src="../assets/pdfimg/top_left_border.png"
              alt="Top Left Corner"
              style={styles.cornerImageTopLeft}
            />
            <img
              src="../assets/pdfimg/top_left_border.png"
              alt="Bottom Right Corner"
              style={styles.cornerImageBottomRight}
            />
            <img
              src="../assets/pdfimg/lims-trademark.png"
              alt="Watermark"
              style={styles.watermark}
            />
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.leftLogos}>
                <img
                  src="../assets/img/lims_logo.png"
                  alt="Lims Logo"
                  style={styles.logo}
                />
                <div style={styles.isoGroup}>
                  <img
                    src="../assets/pdfimg/nabl_logo.png"
                    alt="NABL Logo"
                    style={styles.isoLogo}
                  />
                  <img
                    src="../assets/pdfimg/iso.png"
                    alt="ISO Logo"
                    style={styles.isoLogo}
                  />
                </div>
              </div>

              <div style={styles.badge}>
                <div style={styles.badgeContainer}>
                  <img
                    src="../assets/pdfimg/es-2005.jpg"
                    alt="ISO Logo"
                    style={styles.badgeImage}
                  />
                </div>
              </div>
            </div>
            <div style={styles.title}>AUTHORIZATION CERTIFICATE</div>
            <div style={styles.content}>
              I, hereby authorized that{" "}
              <strong>
                "{rowData?.company_name || franchiseData?.data[0]?.company_name}
                "
              </strong>{" "}
              Proprietor{" "}
              <strong>
                Mr.{" "}
                {rowData?.contact_person ||
                  franchiseData?.data[0]?.contact_person}
              </strong>
              , Contact No.{" "}
              <strong>
                (+91){" "}
                {rowData?.mobile ||
                  franchiseData?.data[0]?.franchise_emp_obj[0]?.mobile}
              </strong>
              , Zone Address:{" "}
              <strong>
                {rowData?.address ||
                  franchiseData?.data[0]?.franchise_emp_obj[0]?.address}
                <span style={{ whiteSpace: "nowrap" }}>
                  {" "}
                  Pin-{rowData?.pincode || franchiseData?.data[0]?.pincode}
                </span>
              </strong>{" "}
              is an authorized Information & Booking Centre of Our Medical
              Organization.
              <br />
              <br />
              He is able to collect all type of Pathological samples on behalf
              of my organization.
              <br />
              <strong>{getFormattedDate()}</strong>
            </div>
            <div style={styles.dateSection}>
              <strong>Date:</strong> {formatDate()}
            </div>
            <div style={styles.signatures}>
              <div style={styles.signatureBlock}>
                <img
                  src="../assets/pdfimg/rishav_sign.jpeg"
                  alt="Signature"
                  height={20}
                  style={{ width: 250, objectFit: "contain" }}
                />

                <div style={styles.signatureInfo}>
                  MR. RISHAV BANERJEE
                  <br />
                  DIRECTOR
                </div>
              </div>
              <div style={styles.signatureBlock}>
                <img
                  src="../assets/pdfimg/arpita_sign.jpg"
                  alt="Signature"
                  height={20}
                  style={{ width: 250, objectFit: "contain" }}
                />
                <div style={styles.signatureInfo}>
                  MRS. ARPITA BANERJEE
                  <br />
                  DIRECTOR
                </div>
              </div>
            </div>
            <div style={styles.footer}>
              12. No. Indra Biswas Road, Belgachia, (near belgachia post office)
              Kolkata – 700037 <br />
              <strong>Branch Office</strong>: Guru Govind Singh link path road,
              Dist. - Patna, P.O. - Azimabad, Bihar - 800009 (Opposite of Soalty
              Hotel, Champaran Meat Shop 2nd floor)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationCertificate;

const styles = {
  body: {
    margin: 0,
    padding: "0",
    backgroundColor: "#fff",
    fontFamily: "'Times New Roman', serif",
  },
  shadowBox: {
    backgroundColor: "#fff",
    padding: 0,
    boxShadow: "0 0 25px rgba(0, 0, 0, 0.2)",
    width: "100%",
    maxWidth: "none",
  },
  watermark: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    zIndex: -1,
    pointerEvents: "none",
    opacity: 1,
  },
  certificate: {
    position: "relative",
    // maxWidth: 900,
    width: "100%",
    margin: "auto",
    padding: "10px 30px 20px 30px",
    background: "#fff",
    border: "6px solid #18B3E9",
    overflow: "visible",
    zIndex: 1,
  },
  innerBorder: {
    position: "absolute",
    top: "6px",
    left: "6px",
    right: "6px",
    bottom: "6px",
    border: "1px solid #18B3E9",
    pointerEvents: "none",
    zIndex: 0,
  },
  cornerImageTopLeft: {
    position: "absolute",
    top: -6,
    left: -6,
    width: 150,
    height: 150,
    zIndex: 2,
    pointerEvents: "none",
  },
  cornerImageBottomRight: {
    position: "absolute",
    bottom: -6,
    right: -6,
    width: 150,
    height: 150,
    transform: "rotate(180deg)",
    zIndex: 2,
    pointerEvents: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
  },
  logo: {
    height: 100,
  },
  leftLogos: {
    display: "flex",
    alignItems: "center",
    gap: 60,
  },
  isoGroup: {
    display: "flex",
    gap: 30,
    alignItems: "center",
    justifyContent: "center",
  },
  isoLogo: {
    height: 100,
    margin: 0,
  },
  badge: {
    textAlign: "right",
  },
  badgeCircle: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "gold",
    color: "#333",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  },
  badgeImage: {
    width: 145,
    height: 153,
    objectFit: "contain",
  },
  badgeContainer: {
    position: "relative",
    display: "inline-block",
  },
  badgeText: {
    position: "absolute",
    top: "35%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center",
    textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
    pointerEvents: "none",
    zIndex: 1,
  },
  title: {
    textAlign: "center",
    fontSize: 40,
    fontWeight: 400,
    marginTop: "30px",
    color: "rgb(16 8 163)",
    position: "relative",
    zIndex: 1,
  },
  content: {
    fontSize: 18,
    lineHeight: 1.6,
    textAlign: "justify",
    padding: 20,
    marginBottom: 30,
    color: "rgb(16 8 163)",
    position: "relative",
    zIndex: 1,
  },
  dateSection: {
    fontSize: 18,
    lineHeight: 1.6,
    textAlign: "left",
    padding: "0 20px",
    marginBottom: 20,
    color: "rgb(16 8 163)",
    position: "relative",
    zIndex: 1,
  },
  signatures: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: 40,
    fontWeight: "bold",
    color: "rgb(16 8 163)",
    position: "relative",
    zIndex: 1,
  },
  signatureBlock: {
    textAlign: "center",
  },
  signatureInfo: {
    borderTop: "2px solid rgb(16, 8, 163)",
    paddingTop: 8,
    marginTop: 8,
    textAlign: "center",
  },
  footer: {
    textAlign: "center",
    marginTop: 20,
    marginBottom: 10,
    fontSize: 14,
    fontStyle: "italic",
    color: "rgb(16 8 163)",
    position: "relative",
    zIndex: 1,
  },
};
